import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Home.scss";

import {
  fetchQueues,
  joinQueue,
  startSearching,
  setModality,
  leaveQueue,
} from "../redux/actions/queues";
import { getBalance, getNotifications } from "../redux/actions/session";
import { redirectIfInGame } from "../redux/actions/game";
import { setFetchQeueIntervalId } from "../redux/actions/communication";
import { hapticLight, hapticMedium } from "../utils/haptic";

import Loading from "./loading/Loading";
import WhatsAppButton from "./whatsAppButton/WhatsAppButton";
import Menu from "./menuDesktop/MenuDesktop";
import MobileMenu from "./menuMobile/MenuMobile";
import SideButtonDrag from "./sideButtonDrag/SideButtonDrag";
import FooterPrivacidade from "./footer-privacidade/footerPrivacidade";
import QueueRow from "./queueRow/QueueRow";
import { MAINTENENCE_MESSAGE_ON } from "../utils/constants";

const Home = () => {
  const queues = useSelector((state) => state.queues.list);
  const queueId = useSelector((state) => state.session.queueId);
  const token = useSelector((state) => state.session.token);
  const modality = useSelector((state) => state.queues.modality);
  const dispatch = useDispatch();

  const shouldFetch = useRef(true);

  const popularId = useMemo(() => {
    if (queues.length === 0) return null;
    let max = 0,
      id = null;
    queues.forEach((q) => {
      if (q.users.length > max) {
        max = q.users.length;
        id = q.id;
      }
    });
    return max > 0 ? id : null;
  }, [queues]);

  const handleUnload = () => {
    dispatch(leaveQueue());
  };

  useEffect(() => {
    window.addEventListener("beforeunload", handleUnload);

    if (shouldFetch.current) {
      shouldFetch.current = false;
      dispatch(fetchQueues());
      if (queueId) {
        dispatch(startSearching());
      }

      const fetchQeuesIntervalId = setInterval(
        () => dispatch(fetchQueues()),
        5000
      );

      let fetchNotificationsIntervalId;
      if (token) {
        dispatch(getBalance());
        dispatch(getNotifications());
        // Se o jogador deu F5 / saiu do app no meio de uma partida,
        // redireciona de volta pra /game ao voltar pra Home.
        dispatch(redirectIfInGame());
        dispatch(setFetchQeueIntervalId(fetchQeuesIntervalId));
        // Polling só faz sentido com token — sem ele as requests retornam 401.
        fetchNotificationsIntervalId = setInterval(
          () => dispatch(getNotifications()),
          3000
        );
      }

      return () => {
        window.removeEventListener("beforeunload", handleUnload);
        clearInterval(fetchQeuesIntervalId);
        if (fetchNotificationsIntervalId) {
          clearInterval(fetchNotificationsIntervalId);
        }
      };
    }
  }, [dispatch]);

  useEffect(() => {
    const newIntervalId = setInterval(() => dispatch(fetchQueues()), 5000);
    return () => clearInterval(newIntervalId);
  }, [modality, dispatch]);

  const handleModalityClick = (newModality) => {
    hapticLight();
    dispatch(setModality(newModality));
    dispatch(fetchQueues());
  };

  return (
    <div className="home-page">
      <Menu />
      <Loading />

      {MAINTENENCE_MESSAGE_ON && (
        <div className="maintenance-banner">
          <span
            className="maintenance-banner-icon"
            role="img"
            aria-label="manutenção"
          >
            ⚠️
          </span>
          <span className="maintenance-banner-text">
            Servidor em manutenção. Voltaremos em poucas horas com as filas,
            saques e depósitos!
          </span>
        </div>
      )}

      <main className="home-page__content">
        <header className="home-page__title">
          <h1>Sente à Mesa, Parceiro!</h1>
          <p>Escolha sua mesa e mostre quem manda no truco</p>
        </header>

        <div className="home-page__tabs">
          <button
            type="button"
            className={`home-page__tab ${
              modality === "paulista" ? "home-page__tab--active" : ""
            }`}
            onClick={() => handleModalityClick("paulista")}
          >
            Paulista
          </button>
          <button
            type="button"
            className={`home-page__tab ${
              modality === "mineiro" ? "home-page__tab--active" : ""
            }`}
            onClick={() => handleModalityClick("mineiro")}
          >
            Mineiro
          </button>
        </div>

        <div className="home-page__section-title">
          <span className="home-page__section-line" />
          <span>Mesas disponíveis</span>
          <span className="home-page__section-line" />
        </div>

        <div className="home-page__list">
          {queues.map((queue, idx) => (
            <QueueRow
              key={queue.id}
              queue={queue}
              index={idx}
              isPopular={queue.id === popularId}
              token={token}
              onJoin={(id, value) => {
                hapticMedium();
                dispatch(joinQueue(id, value));
              }}
            />
          ))}
        </div>

      </main>

      <footer className="home-page__footer">
        <span className="home-page__footer-brand">trucoxp©</span>
        <span>{new Date().getFullYear()}</span>
        <span className="home-page__footer-sep">•</span>
        <a
          href="/termos-e-condicoes.html"
          target="_blank"
          rel="noopener noreferrer"
          className="home-page__footer-link"
        >
          Termos de Uso
        </a>
        <span className="home-page__footer-sep">|</span>
        <a
          href="/politica-de-privacidade.html"
          target="_blank"
          rel="noopener noreferrer"
          className="home-page__footer-link"
        >
          Política de Privacidade
        </a>
      </footer>

      <WhatsAppButton whatsAppClass="whatsAppClass" />
      <MobileMenu />
      <SideButtonDrag />
      {!token && <FooterPrivacidade />}
    </div>
  );
};

export default Home;
